import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Breadcrumbs, SectionHead } from "@/components/Shared";
import { SERVICES, PROVINCES, CITIES, SECTORS, INSIGHTS } from "@/data/site";
import { FR_CITIES, FR_SERVICES, frUrl } from "@/data/fr";
import { url, metaSitemap, citiesInProv } from "@/data/content";

const Li = ({ to, children }) => (
  <li>
    <Link to={to} className="text-sm text-slate-400 hover:text-amber-400 transition-colors">{children}</Link>
  </li>
);

export default function SitemapPage() {
  const meta = metaSitemap();
  const total = 7 + SERVICES.length * (1 + PROVINCES.length + CITIES.length) + PROVINCES.length + CITIES.length + SECTORS.length + INSIGHTS.length;
  return (
    <div data-testid="sitemap-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14">
      <Seo title={meta.title} description={meta.description} path="/sitemap/" />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Sitemap", path: "/sitemap/" }]} />
      <SectionHead as="h1" eyebrow="HTML Sitemap" title="Every page on this site" desc={`${total} pages: services, provinces, cities, sectors and insights. A machine-readable version lives at /sitemap.xml.`} />
      <div className="space-y-12" data-testid="sitemap-link-list">
        <section>
          <h2 className="text-lg font-bold font-display text-amber-400 mb-4">Main Pages</h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <Li to="/">Home</Li>
            <Li to="/about/">About Us</Li>
            <Li to="/contact/">Contact</Li>
            <Li to="/services/">All Security Services</Li>
            <Li to="/locations/">All Locations</Li>
            <Li to="/sectors/">Industry Sectors</Li>
            <Li to="/insights/">Insights & Guides</Li>
            <Li to="/sitemap/">Sitemap</Li>
          </ul>
        </section>
        <section>
          <h2 className="text-lg font-bold font-display text-amber-400 mb-4">Security Services ({SERVICES.length})</h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {SERVICES.map((s) => <Li key={s.slug} to={url.service(s)}>{s.name}</Li>)}
          </ul>
        </section>
        <section>
          <h2 className="text-lg font-bold font-display text-amber-400 mb-4">Provinces & Territories ({PROVINCES.length})</h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {PROVINCES.map((p) => <Li key={p.slug} to={url.prov(p)}>Security Guard Services {p.name}</Li>)}
          </ul>
        </section>
        <section>
          <h2 className="text-lg font-bold font-display text-amber-400 mb-4">Cities ({CITIES.length})</h2>
          {PROVINCES.map((p) => {
            const cities = citiesInProv(p.slug);
            if (!cities.length) return null;
            return (
              <div key={p.slug} className="mb-6">
                <h3 className="text-sm font-mono uppercase tracking-widest text-slate-500 mb-2">{p.name}</h3>
                <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                  {cities.map((c) => <Li key={c.slug} to={url.city(c)}>Security Guards {c.name}</Li>)}
                </ul>
              </div>
            );
          })}
        </section>
        {SERVICES.map((s) => (
          <section key={s.slug}>
            <h2 className="text-lg font-bold font-display text-amber-400 mb-4">{s.name} by Location</h2>
            <h3 className="text-sm font-mono uppercase tracking-widest text-slate-500 mb-2">By Province & Territory</h3>
            <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-5">
              {PROVINCES.map((p) => <Li key={p.slug} to={url.serviceProv(s, p)}>{s.name} {p.name}</Li>)}
            </ul>
            <h3 className="text-sm font-mono uppercase tracking-widest text-slate-500 mb-2">By City</h3>
            <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {CITIES.map((c) => <Li key={c.slug} to={url.serviceCity(s, c)}>{s.name} {c.name}</Li>)}
            </ul>
          </section>
        ))}
        <section>
          <h2 className="text-lg font-bold font-display text-amber-400 mb-4">Industry Sectors ({SECTORS.length})</h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {SECTORS.map((x) => <Li key={x.slug} to={url.sector(x)}>{x.name} Security</Li>)}
          </ul>
        </section>
        <section>
          <h2 className="text-lg font-bold font-display text-amber-400 mb-4">Insights & Guides ({INSIGHTS.length})</h2>
          <ul className="grid sm:grid-cols-2 gap-2">
            {INSIGHTS.map((a) => <Li key={a.slug} to={url.article(a)}>{a.title}</Li>)}
          </ul>
        </section>
        <section>
          <h2 className="text-lg font-bold font-display text-amber-400 mb-4">Pages en francais (Quebec)</h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-6">
            <Li to="/fr/">Accueil — Services de securite au Quebec</Li>
            <Li to="/fr/quebec/">Securite au Quebec (province)</Li>
          </ul>
          <h3 className="text-sm font-mono uppercase tracking-widest text-slate-500 mb-2">Villes du Quebec</h3>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-6">
            {FR_CITIES.map((c) => <Li key={c.slug} to={frUrl.city(c)}>Gardiens de securite {c.name}</Li>)}
          </ul>
          {SERVICES.map((s) => (
            <div key={s.slug} className="mb-6">
              <h3 className="text-sm font-mono uppercase tracking-widest text-slate-500 mb-2">{FR_SERVICES[s.slug].name}</h3>
              <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                <Li to={frUrl.service(s)}>{FR_SERVICES[s.slug].name} (national)</Li>
                <Li to={frUrl.serviceProv(s)}>{FR_SERVICES[s.slug].name} Quebec</Li>
                {FR_CITIES.map((c) => <Li key={c.slug} to={frUrl.serviceCity(s, c)}>{FR_SERVICES[s.slug].name} {c.name}</Li>)}
              </ul>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
